<template>
  <div class="flex flex-col items-start">
    <InputLabel v-if="props.label" :optional="!props.required">
      {{ props.label }}
    </InputLabel>
    <ClientOnly>
      <VueSelect
        :id="id"
        class="select w-full"
        :clear-search-on-blur="() => !hasTouch"
        :filterable="false"
        label="name"
        :loading="props.loading"
        :options="props.schools"
        placeholder="Start typing..."
        :value="selectedSchool"
        @update:model-value="onUpdate"
        @search="onSearch"
        @open="isOpen = true"
        @close="isOpen = false"
      >
        <template #no-options> No matching schools. </template>
        <template #option="school: School | null">
          <span v-if="school" class="inline-block w-full truncate">
            <span v-text="school.name" />
            <span class="opacity-25">
              ({{ school.location.city }}, {{ school.location.state }})</span
            >
          </span>
        </template>
      </VueSelect>
    </ClientOnly>
    <InputError :error="errorMessage" />
  </div>
</template>

<script setup lang="ts">
import type { School } from '#payload/types'
import type { BaseInputProps } from '@/components/base/input/types'
import type { InputProps } from './types'

interface InputSchoolProps extends BaseInputProps, InputProps {
  loading?: boolean
  schools: School[]
}

const props = withDefaults(defineProps<InputSchoolProps>(), { loading: false })
const { id, value, errorMessage } = useInput<School | null>(props.name)
const { hasTouch } = useTouch()

const selectedSchool = computed(() => {
  return props.schools.find((school) => school.id === value.value?.id) || null
})

const isOpen = ref(false)

const onUpdate = (school: School) => {
  value.value = school || null
}

const emit = defineEmits<{
  search: [search: string]
}>()

const onSearch = (search: string) => {
  if (search || isOpen.value) {
    emit(
      'search',
      search
        .replace(/'/g, '')
        .replace(/[^\w\s]/g, ' ')
        .replace(/\s+/, ' ')
        .toLowerCase(),
    )
  }
}
</script>

<style scoped lang="postcss">
.select.v-select:deep(.vs__open-indicator) {
  @apply hidden;
}
</style>
